import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { controlCenterApi, type AccountDetail, type StaffNote } from '../../services/controlCenterApi'
import { useStaffAuth } from '../../contexts/StaffAuthContext'

interface AccountContext {
  account: AccountDetail
}

export function AccountNotesTab(): React.ReactElement {
  const { account } = useOutletContext<AccountContext>()
  const { staff } = useStaffAuth()
  const [notes, setNotes] = useState<StaffNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [newNote, setNewNote] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      setError('')
      try {
        const data = await controlCenterApi.getAccountNotes(account.id)
        if (!cancelled) setNotes(data)
      } catch {
        if (!cancelled) setError('Failed to load notes')
      }
      if (!cancelled) setIsLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [account.id])

  const handleCreate = async () => {
    if (!newNote.trim()) return
    setIsSaving(true)
    try {
      const note = await controlCenterApi.createAccountNote(account.id, newNote.trim())
      setNotes(prev => [note, ...prev])
      setNewNote('')
    } catch {
      setError('Failed to save note')
    }
    setIsSaving(false)
  }

  const handleUpdate = async (noteId: string) => {
    if (!editBody.trim()) return
    try {
      const updated = await controlCenterApi.updateAccountNote(account.id, noteId, editBody.trim())
      setNotes(prev => prev.map(n => n.id === noteId ? updated : n))
      setEditingId(null)
    } catch {
      setError('Failed to update note')
    }
  }

  const handleDelete = async (noteId: string) => {
    try {
      await controlCenterApi.deleteAccountNote(account.id, noteId)
      setNotes(prev => prev.filter(n => n.id !== noteId))
    } catch {
      setError('Failed to delete note')
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading notes...</span>
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* New Note Form */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6">
        <textarea
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
          placeholder="Add an internal note..."
          rows={3}
          className="w-full bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm p-3 resize-none"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleCreate}
            disabled={!newNote.trim() || isSaving}
            className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isSaving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-10 h-10 text-slate-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <p className="text-slate-400 text-sm mt-2">No notes yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <div key={note.id} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              {editingId === note.id ? (
                <div>
                  <textarea
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    rows={3}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm p-3 resize-none outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="flex gap-2 mt-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdate(note.id)}
                      className="px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-white whitespace-pre-wrap">{note.body}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                    <div className="text-xs text-slate-500">
                      <span className="font-medium text-slate-400">{note.staff_name}</span>
                      <span className="mx-1.5">&middot;</span>
                      {new Date(note.created_at).toLocaleString()}
                      {note.updated_at !== note.created_at && (
                        <span className="ml-1 text-slate-600">(edited)</span>
                      )}
                    </div>
                    {staff?.id === note.staff_id && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setEditingId(note.id); setEditBody(note.body) }}
                          className="text-xs text-slate-400 hover:text-white transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(note.id)}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
